export type BugReportFeatureCatalogEntry = {
  key: string;
  label: string;
  userHelp: string;
  aiBrief: string;
};

export const BUG_REPORT_TUMU_LABEL_DATA = {
  understandTheNumbers: "Understand the numbers",
  trustTheNumbers: "Trust the numbers",
  makeDecisionsFromTheNumbers: "Make decisions from the numbers",
  useHcRegularly: "Use HC regularly",
} as const;

export const BUG_REPORT_FEATURE_CATALOG_DATA = [
  {
    key: "homepage",
    label: "Homepage",
    userHelp:
      "Use this for issues on the Home dashboard after login (tiles, navigation, summary sections).",
    aiBrief:
      "Post-login homepage with navigation tiles and quick links into core features (Reports, Budget, Targets, Deep Dive). Look: apps/web/src/pages/Homepage.tsx, apps/web/src/router.tsx, docs/guides/MANUAL_TESTING_ONBOARDING.md.",
  },
  {
    key: "onboarding-login",
    label: "Onboarding & Login",
    userHelp:
      "Use this for login/account creation and onboarding steps (connect provider, setup flow).",
    aiBrief:
      "Account creation/login + onboarding steps to connect a provider and finish setup. Look: apps/web/src/pages/LoginPage/*, apps/web/src/pages/onboarding/*, apps/web/src/router.tsx, docs/guides/MANUAL_TESTING_ONBOARDING.md.",
  },
  {
    key: "account-settings",
    label: "Account Settings",
    userHelp:
      "Use this for issues on Account Settings (logout, settings UI, subscription settings / Xero App Store settings).",
    aiBrief:
      "Account Settings (/settings/account): account settings surface (regular vs Xero App Store settings) and logout. Look: apps/web/src/features/settings/account/AccountSettingsPage.tsx, apps/web/src/features/settings/SettingsShell.tsx, apps/web/src/components/settings/RegularAccountSettings.tsx, apps/web/src/components/settings/XeroAppStoreSettings.tsx.",
  },
  {
    key: "organisation-settings",
    label: "Organisation Settings",
    userHelp:
      "Use this for Organisation Settings issues (org name/logo, users/invites, accounting basis / setup actions).",
    aiBrief:
      "Organisation Settings (/settings/organisation): manage org name/logo, user invites, and accounting basis / financial setup actions. Look: apps/web/src/features/settings/organisation/OrganisationSettingsTab.tsx, apps/web/src/features/settings/users/AccountUsersTab.tsx, apps/web/src/hooks/sync-v2/useSSESync.ts, apps/web/src/redux/rtkQuery/organisations.ts.",
  },
  {
    key: "provider-connection",
    label: "Provider Connection",
    userHelp:
      "Use this for issues connecting/reconnecting Xero/QuickBooks or provider login redirects (OAuth popups, disconnect states).",
    aiBrief:
      "Provider connection/reconnect flow for Xero/QuickBooks (OAuth redirects, connect/login routes, disconnect handling). Look: apps/web/src/pages/Account/HandleProviderResponse.tsx, apps/web/src/pages/Account/AccountBase.tsx, apps/web/src/pages/Account/*Disconnected.tsx, services/api/src/service/tokenManagement/TokenRefreshService.ts.",
  },
  {
    key: "budget-editor",
    label: "Budget Editor",
    userHelp:
      "Use this for issues editing monthly budgets (tables, validation, autofill, saving) across revenue/expenses/cashflow.",
    aiBrief:
      "Monthly budget editing for revenue/expenses/cashflow (drafts, validation, save). Look: apps/web/src/pages/BudgetEditor/StandardBudgetEditor.tsx, apps/web/src/hooks/editing/useBudgetEditState.ts, docs/app/readmes/BUDGET_EDITOR_ARCHITECTURE.md.",
  },
  {
    key: "reports-revenue",
    label: "Reports — Revenue",
    userHelp:
      "Use this when the issue is on the Revenue report page (charts, breakdowns, date selection).",
    aiBrief:
      "Revenue report page (charts, breakdowns, date selection). Look: apps/web/src/pages/reports/Revenue.tsx, apps/web/src/hooks/reports/useRevenue.ts, apps/web/src/hooks/reports/useReportDate.ts, docs/app/readmes/DATA_SERIES_GUIDE.md.",
  },
  {
    key: "reports-expenses",
    label: "Reports — Expenses",
    userHelp:
      "Use this when the issue is on the Expenses report page (charts, categories, breakdowns, date selection).",
    aiBrief:
      "Expenses report page (charts, categories, breakdowns, date selection). Look: apps/web/src/pages/reports/Expenses.tsx, apps/web/src/hooks/reports/useExpenses.ts, apps/web/src/hooks/reports/useReportDate.ts, docs/app/readmes/DATA_SERIES_GUIDE.md.",
  },
  {
    key: "reports-profit",
    label: "Reports — Profit",
    userHelp:
      "Use this when the issue is on the Profit report page (summary, breakdowns, comparisons).",
    aiBrief:
      "Profit report page (summary, comparisons, breakdowns). Look: apps/web/src/pages/reports/Profit.tsx, apps/web/src/hooks/reports/useProfit.ts, docs/app/readmes/DATA_SERIES_GUIDE.md.",
  },
  {
    key: "reports-cashflow",
    label: "Reports — Cashflow",
    userHelp:
      "Use this when the issue is on the Cashflow report page (net cash, bank balances, runway/targets).",
    aiBrief:
      "Cashflow report (net cash, bank balances, runway/targets). Look: apps/web/src/pages/reports/Cashflow.tsx, apps/web/src/hooks/reports/useCashflow.ts, docs/app/readmes/DATA_SERIES_GUIDE.md.",
  },
  {
    key: "reports-budgeting",
    label: "Reports — Budgeting",
    userHelp:
      "Use this when the issue is on the Budgeting report page (budget accuracy widgets, period selection).",
    aiBrief:
      "Budgeting report page (/reports/my-budgeting): budget accuracy and budgeting metrics widgets, month/period selection. Look: apps/web/src/pages/reports/Budgeting.tsx, apps/web/src/hooks/useReportPagePreferences.ts, apps/web/src/components/widgets/small/*/BudgetAccuracy.tsx.",
  },
  {
    key: "reports-balance-sheet",
    label: "Reports — Balance Sheet",
    userHelp:
      "Use this when the issue is on the Balance Sheet report page (assets/liabilities/equity).",
    aiBrief:
      "Balance sheet report (assets/liabilities/equity). Look: apps/web/src/pages/reports/BalanceSheet.tsx, apps/web/src/hooks/reports/useReport.ts, docs/app/readmes/DATA_SERIES_GUIDE.md.",
  },
  {
    key: "reports-custom-reports",
    label: "Reports — Custom Reports",
    userHelp:
      "Use this when the issue is in Custom Reports (customer-economics reports, report switching/comparison/export).",
    aiBrief:
      "Custom Reports surface (customer-economics reports + report switching/comparison). Look: apps/web/src/pages/reports/CustomerReports.tsx, apps/web/src/features/customer-economics/report/custom-reports/CustomerReportsPage.tsx, apps/web/src/features/customer-economics/report/custom-reports/myCustomReportsComparisonContext.tsx, docs/plans/Ideas/NewCustomReports.txt.",
  },
  {
    key: "reports-customers",
    label: "Reports — Customers",
    userHelp:
      "Use this when the issue is on the Customers report page (customer lists, receivables metrics).",
    aiBrief:
      "Customers report metrics now render through Custom Reports. Look: apps/web/src/pages/reports/CustomerReports.tsx, apps/web/src/features/customer-economics/report/custom-reports/CustomerReportsPage.tsx, apps/web/src/hooks/reports/useCustomer.ts, docs/app/readmes/DATA_SERIES_GUIDE.md.",
  },
  {
    key: "reports-diagnostics",
    label: "Reports — Diagnostics",
    userHelp:
      "Use this for issues on the Reports Diagnostics page (debug/all): missing sections, copy/download, or incorrect values.",
    aiBrief:
      "Reports Diagnostics page (/reports/debug/all): generates a markdown snapshot of report data for QA/debugging (copy/download). Look: apps/web/src/pages/ReportsDiagnosticsPage.tsx, apps/web/src/reporting/reportDefinitions.ts, apps/web/src/reporting/buildMarkdownReport.ts.",
  },
  {
    key: "in-app-mail",
    label: "In‑App Mail",
    userHelp:
      "Use this for issues with the in-app message/inbox panel (showing, unread badges, dismissals) inside Reports.",
    aiBrief:
      "In-app mail/inbox panel shown in Reports (notifications/messages). Look: apps/web/src/pages/InAppMail.tsx, apps/web/src/pages/reports/ReportsBase.tsx.",
  },
  {
    key: "deep-dive",
    label: "Deep Dive",
    userHelp:
      "Use this for issues creating/editing Deep Dive scenarios or viewing Trends/Snapshot/Breakdown.",
    aiBrief:
      "Deep Dive scenarios: explore revenue/expense/profit drivers with trend/snapshot/breakdown views. Look: apps/web/src/pages/DeepDive/DeepDive.tsx, apps/web/src/pages/DeepDive/Pages/DeepDiveEditorPage.tsx, docs/plans/FigmaDeepDive/, docs/guides/MANUAL_TESTING_ONBOARDING.md.",
  },
  {
    key: "scenario-calculator",
    label: "Scenario Calculator",
    userHelp:
      "Use this for issues in the Scenario Calculator flow (steps, factors, inputs, report).",
    aiBrief:
      "Scenario Calculator step flow for planning adjustments vs baseline and generating a scenario report. Look: apps/web/src/pages/ScenarioCalculator/ScenarioCalculator.tsx, apps/web/src/redux/slices/ScenarioSlice.ts, docs/app/readmes/SCENARIO_CALCULATOR.md.",
  },
  {
    key: "targets",
    label: "My Targets",
    userHelp:
      "Use this for issues setting or viewing Targets (profit/cash/runway), including save/progress UI.",
    aiBrief:
      "Targets hub (profit targets today; cash/runway targets may be configured in Cashflow report). Look: apps/web/src/features/targets/TargetsRoute.tsx, apps/web/src/features/targets/TargetsDashboardView.tsx, apps/web/src/pages/reports/Cashflow.tsx, docs/features/my-targets/SPEC_PROVISIONAL.md.",
  },
  {
    key: "financial-health-check",
    label: "Financial Health Check",
    userHelp:
      "Use this for issues in Financial Health Check (settings/view, PDF generation, share/email/reminders).",
    aiBrief:
      "Financial Health Check reporting surface that generates a PDF snapshot, share links, and reminders. Look: apps/web/src/features/financial-health-checks/FinancialHealthChecksDashboard.tsx, services/api/src/controllers/financialHealthChecksController.ts, docs/app/readmes/FINANCIAL_HEALTH_CHECK.md.",
  },
  {
    key: "pdf-reports",
    label: "PDF Reports",
    userHelp:
      "Use this for issues in PDF Reports (dashboard, builder wizard, PDF export, share links).",
    aiBrief:
      "PDF Reports wizard (builder → PDF generation → share token). Look: apps/web/src/pages/reporting/PdfReportsDashboard.tsx, apps/web/src/pages/reports/builder/ReportBuilder.tsx, services/api/src/controllers/pdfReportsController.ts, docs/app/readmes/PDF_REPORTS_ARCHITECTURE.md, docs/plans/FigmaPdfReports/.",
  },
  {
    key: "partner-dashboard",
    label: "Partner Dashboard",
    userHelp:
      "Use this for Partner Portal issues (partner dashboard, invites, linked clients).",
    aiBrief:
      "Partner Portal (Partners v2): partner-scoped dashboard, invites, and linked org management. Look: apps/web/src/pages/Partner/PartnerDashboardV2.tsx, services/api/src/controllers/partnerController.ts, docs/features/PARTNER_DASHBOARD.md.",
  },
] as const satisfies readonly BugReportFeatureCatalogEntry[];

export type BugReportFeatureDataKey =
  (typeof BUG_REPORT_FEATURE_CATALOG_DATA)[number]["key"];

export const BUG_REPORT_TUMU_FEATURE_GROUP_KEYS = {
  trustTheNumbers: [
    "reports-revenue",
    "reports-expenses",
    "reports-profit",
    "reports-cashflow",
    "reports-budgeting",
    "reports-balance-sheet",
    "reports-custom-reports",
    "reports-customers",
    "reports-diagnostics",
    "financial-health-check",
    "pdf-reports",
  ],
  makeDecisionsFromTheNumbers: [
    "budget-editor",
    "deep-dive",
    "scenario-calculator",
    "targets",
  ],
  useHcRegularly: [
    "homepage",
    "onboarding-login",
    "account-settings",
    "organisation-settings",
    "provider-connection",
    "in-app-mail",
    "partner-dashboard",
  ],
} as const satisfies Record<string, readonly BugReportFeatureDataKey[]>;
